import React, { FC, useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import FancyRadio from '@rover/components/src/formFields/FancyRadioGroup/FancyRadio';
import RowList from '@rover/components/src/RowList';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import ClickableInput from '@rover/kibble/patterns/ClickableInput';
import { SearchInterstitialUpdateServiceType } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Collections/Wizard/analyticsEvents';
import TruncatedText from '@rover/react-lib/src/pages/search/SearchPage/components/TruncatedText';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import type { SelectOption } from '@rover/types/src/SelectOption';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';

import ServiceIcon from './ServiceIcon';

const StyledFancyRadio = styled(FancyRadio)`
  margin-bottom: 0;
  padding-right: 0;
`;

const getServiceNameValue = (
  serviceType: ServiceType,
  serviceTypeOptions: SelectOption[]
): string => {
  const option = serviceTypeOptions.find(({ value }) => value === serviceType);
  return `${option?.title} • ${option?.subtitle}`;
};

type MobileServiceSelectorProps = {
  serviceTypeOptions: ServiceTypeOption[];
  selected: ServiceType;
  onChange: ({ serviceType }: { serviceType: ServiceType }) => void;
};

const MobileServiceSelector: FC<MobileServiceSelectorProps> = ({
  serviceTypeOptions,
  selected,
  onChange,
}) => {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);

  const serviceTypeValue = useMemo(
    () => getServiceNameValue(selected, serviceTypeOptions),
    [selected, serviceTypeOptions]
  );

  const handleChange = useCallback(
    (serviceTypeSlug: string): void => {
      const isDifferentServiceType = selected !== serviceTypeSlug;
      const slug = serviceTypeSlug as ServiceType;
      if (isDifferentServiceType) {
        onChange({ serviceType: slug });
        emitAnalyticsEvent(new SearchInterstitialUpdateServiceType({ serviceType: slug }));
      }
      setIsOpen(false);
    },
    [selected, onChange]
  );

  const handleClose = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <ClickableInput inputLabel={t`Looking for`} onClick={() => setIsOpen(true)}>
        <Flex alignItems="center">
          <Flex alignItems="center" marginRight="2x">
            <ServiceIcon serviceType={selected} />
          </Flex>
          <TruncatedText>{serviceTypeValue}</TruncatedText>
        </Flex>
      </ClickableInput>
      <BottomSheet
        onRequestClose={handleClose}
        isOpen={isOpen}
        style={{ content: { outline: 'none' } }}
      >
        <Heading fontWeight="semibold" textAlign="center" paddingY="2x">
          {t`What service do you need?`}
        </Heading>
        <Box width="100%" mt="6x">
          <RowList>
            {serviceTypeOptions.map(({ title, subtitle, value }) => (
              <RowList.Row
                key={value}
                role="button"
                ariaLabel={`${title as string}, ${subtitle as string}${
                  selected === value ? `, ${t`selected`}` : ''
                }`}
                label={
                  <Heading textColor="primary" marginBottom="0x" fontWeight="semibold">
                    {title as string}
                  </Heading>
                }
                sublabel={
                  <Text size="100" textColor="secondary" marginBottom="0x">
                    {subtitle as string}
                  </Text>
                }
                leftItem={<ServiceIcon serviceType={value} />}
                onClick={() => handleChange(value)}
                rightItem={
                  <Box aria-hidden="true">
                    <StyledFancyRadio
                      id={value}
                      value={`${selected === value}`}
                      checked={selected === value}
                      hideLabel
                      label={`${title as string} - ${subtitle as string}`}
                      validationType="inline"
                      name="ServiceType"
                      onChange={() => handleChange(value)}
                      optionWidth="24"
                    />
                  </Box>
                }
              />
            ))}
          </RowList>
        </Box>
      </BottomSheet>
    </>
  );
};

export default MobileServiceSelector;
